import { useEffect, useState } from 'react';
import { useAuthContext } from '../../context/AuthContext';
import { LurkerAccess, LurkerApiService } from '../services/LurkerApiService';

const NO_ACCESS: LurkerAccess = { owner: false };

/**
 * What the signed-in user may do in Lurker; no owner rights until the server says so for this
 * user, or when it cannot be asked. The server refuses an owner's writes from anyone else either
 * way. `checked` says the server answered, or failed to, for this user; `answered` that it answered.
 */
export function useLurkerAccess(): LurkerAccess & { checked: boolean; answered: boolean } {
  const { token, user } = useAuthContext();
  const email = user?.email ?? '';
  // The answer is kept with the user and token it was asked with (null access when the check
  // failed), so another user starts without rights.
  const [answer, setAnswer] = useState<{
    email: string;
    token: string;
    access: LurkerAccess | null;
  } | null>(null);
  useEffect(() => {
    const controller = new AbortController();
    LurkerApiService.fetchAccess(token, controller.signal)
      .then((access) => setAnswer({ email, token, access }))
      .catch(() => !controller.signal.aborted && setAnswer({ email, token, access: null }));
    return () => controller.abort();
  }, [token, email]);
  // A refreshed token keeps the user's last answer until the new one arrives; a failure counts for
  // its own token only.
  const current =
    answer && answer.email === email && (answer.token === token || answer.access) ? answer : null;
  return {
    ...(current?.access ?? NO_ACCESS),
    checked: current !== null,
    answered: !!current?.access,
  };
}
